import api from './api';
import type { OutputChannelConfig } from './output-channels-api';

// ============================================================================
// TYPES (mirror backend src/services/pulse/types.ts)
// ============================================================================

export type PulseAccountState = 'active' | 'paused' | 'eligible' | 'ineligible';
export type PulseIneligibleReason = 'sync_too_infrequent' | 'not_ingestion_type';
export type PulseSeverity = 'Caution' | 'Dangerous' | 'ALERT!';

export interface PulseJobSummary {
  id: string;
  status: string;
  lastRun: string | null;
  nextRun: string | null;
  schedule: string;
  scheduleLabel: string;
  errorCount: number;
  lastError: string | null;
}

export interface PulseAccount {
  accountId: string;
  accountName: string;
  accountType: string;
  dataSourceId: string;
  dataSourceName: string;
  sourceType: string;
  /** Owning business — Pulse spans every business in the workspace (R11). */
  organizationId: string;
  organizationName: string;
  syncFrequency: string | null;
  syncFrequencyLabel: string;
  lastSyncAt: string | null;
  state: PulseAccountState;
  ineligibleReason?: PulseIneligibleReason;
  definitionJob?: PulseJobSummary;
  executionJob?: PulseJobSummary;
  signalsLastRefreshedAt?: string | null;
}

export interface PulseAccountsResponse {
  active: PulseAccount[];
  paused: PulseAccount[];
  eligible: PulseAccount[];
  ineligible: PulseAccount[];
}

export interface PulseAnalyzeResponse {
  jobId: string;
  status: 'running';
}

export interface PulseActivateResponse {
  activated: boolean;
  executionJobId: string;
  executionScheduleLabel: string;
}

export interface PulseSignalValue {
  value: string;
  displayName?: string;
  tier: string;
  dailyAvgLast7Days: number;
}

export interface PulseSignalField {
  field: string;
  analysisLevel: 'transactions' | 'transaction_details';
  values: PulseSignalValue[];
}

export interface PulseSignalsResponse {
  status: 'not_analyzed' | 'analyzing' | 'failed' | 'analyzed';
  error?: string;
  analyzedAt?: string | null;
  baseline?: { tier: string; dailyAvgLast7Days: number };
  fields?: PulseSignalField[];
  /** Field keys currently monitored (empty = baseline only); used to pre-check the edit UI. */
  monitoredFields?: string[];
  /** Current alert delivery channel (null = in-app only); used to pre-select the edit UI. */
  outputChannel?: OutputChannelConfig | null;
}

export interface PulseAnomalyCard {
  resultId: string;
  severity: PulseSeverity;
  investigated: boolean;
  headline: string;
  natureChips: string[];
  impact: number | null;
  impactIsEstimate: boolean;
  signalLabel: string;
  detectedAt: string;
  lastTransactionAt: string | null;
  lastSyncAt: string | null;
  threadUrl: string | null;
  accountId: string;
  accountName: string;
  dataSourceName: string;
  /** Owning business — the feed spans every business in the workspace (R11). */
  organizationId: string;
  organizationName: string;
}

export interface PulseAnomaliesResponse {
  items: PulseAnomalyCard[];
  nextCursor: string | null;
}

// ============================================================================
// API SERVICE
// ============================================================================

export const pulseApi = {
  async listAccounts(): Promise<PulseAccountsResponse> {
    const response = await api.get('/api/pulse/accounts');
    return response.data;
  },

  async getAccount(accountId: string): Promise<PulseAccount> {
    const response = await api.get(`/api/pulse/accounts/${accountId}`);
    return response.data;
  },

  async analyze(accountId: string): Promise<PulseAnalyzeResponse> {
    const response = await api.post(`/api/pulse/accounts/${accountId}/analyze`);
    return response.data;
  },

  async getSignals(accountId: string): Promise<PulseSignalsResponse> {
    const response = await api.get(`/api/pulse/accounts/${accountId}/signals`);
    return response.data;
  },

  async activate(
    accountId: string,
    monitoredFields: string[] = [],
    outputChannel: OutputChannelConfig | null = null,
  ): Promise<PulseActivateResponse> {
    const response = await api.post(`/api/pulse/accounts/${accountId}/activate`, {
      monitoredFields,
      outputChannel,
    });
    return response.data;
  },

  async pause(accountId: string): Promise<{ paused: true }> {
    const response = await api.post(`/api/pulse/accounts/${accountId}/pause`);
    return response.data;
  },

  async resume(accountId: string): Promise<{ resumed: true }> {
    const response = await api.post(`/api/pulse/accounts/${accountId}/resume`);
    return response.data;
  },

  async listAccountAnomalies(
    accountId: string,
    opts: { cursor?: string | null; limit?: number } = {},
  ): Promise<PulseAnomaliesResponse> {
    const response = await api.get(`/api/pulse/accounts/${accountId}/anomalies`, {
      params: {
        cursor: opts.cursor ?? undefined,
        limit: opts.limit,
      },
    });
    return response.data;
  },

  async listAnomalies(
    opts: {
      cursor?: string | null;
      limit?: number;
      severity?: PulseSeverity | null;
      /** Narrow the workspace-wide feed to one business. */
      organizationId?: string | null;
    } = {},
  ): Promise<PulseAnomaliesResponse> {
    const response = await api.get('/api/pulse/anomalies', {
      params: {
        cursor: opts.cursor ?? undefined,
        limit: opts.limit,
        severity: opts.severity ?? undefined,
        organizationId: opts.organizationId ?? undefined,
      },
    });
    return response.data;
  },
};
